// ────────────────────────────────────────────────────────────────────
// AtlasScreen — Level 1 of the customer portal drill-down.
//
// Layout:
//   ┌─ Floating header (atop map): / ASSET ATLAS · {tenant}     KPIs  ┐
//   │                                                                 │
//   │   ┌─ Cards (left, vertical) ──┐    Leaflet map (full bleed)    │
//   │   │ Plant cards (hover-bind   │    + bathy-overlay              │
//   │   │ to markers via connector  │    + plant markers              │
//   │   │ line)                     │                                 │
//   │   └───────────────────────────┘                                 │
//   └─────────────────────────────────────────────────────────────────┘
//
// Map default bounds: Thailand (covers Bangkok + Map Ta Phut + offshore).
// ────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { MapContainer, TileLayer, Marker, Polyline, Polygon, Tooltip, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L, { type LatLngBoundsExpression } from "leaflet";
import {
  ArrowUpRight, Anchor, AlertTriangle, CheckCircle2, MinusCircle, MapPin, Search, X,
  // Sector icons
  Layers, Droplet, FlaskConical, Flame, Zap, Ship,
  Building2, Snowflake, Hammer, Database, HelpCircle,
} from "lucide-react";
import { apiClient } from "@/lib/api";
import type { Plant } from "@/lib/types";
import { CLIENT_LOGOS } from "@/lib/clientLogos";   // single source of truth (marker + cards)
import FieldSchematicPanel from "./FieldSchematicPanel";
import { CONCESSION_BLOCKS, blockCodeFromLocation, type ConcessionBlock } from "@/data/concessionBlocks";

// ─────────────────────────────────────────────────────────────────────
// Plant sector classification (heuristic from name / code / client)
// ─────────────────────────────────────────────────────────────────────
type Sector =
  | "ALL" | "REFINERY" | "PETROCHEM" | "GAS_PLANT" | "POWER" | "OFFSHORE"
  | "CEMENT" | "LNG" | "STEEL" | "TANK_FARM" | "OTHER";

// Sector metadata — icon + accent color for the chip + marker tint
const SECTOR_META: Record<Sector, { label: string; icon: typeof Layers; color: string }> = {
  ALL:       { label: "All",       icon: Layers,       color: "#1F4E79" },
  REFINERY:  { label: "Refinery",  icon: Droplet,      color: "#C76B07" },
  PETROCHEM: { label: "Petrochem", icon: FlaskConical, color: "#2E7BE0" },
  GAS_PLANT: { label: "Gas plant", icon: Flame,        color: "#D97706" },
  POWER:     { label: "Power",     icon: Zap,          color: "#7B3FBF" },
  OFFSHORE:  { label: "Offshore",  icon: Ship,         color: "#0F766E" },
  CEMENT:    { label: "Cement",    icon: Building2,    color: "#6B7280" },
  LNG:       { label: "LNG",       icon: Snowflake,    color: "#0EA5E9" },
  STEEL:     { label: "Steel",     icon: Hammer,       color: "#4B5563" },
  TANK_FARM: { label: "Tank farm", icon: Database,     color: "#15803D" },
  OTHER:     { label: "Other",     icon: HelpCircle,   color: "#5A6B82" },
};

// Inline SVG path content (24×24 viewBox) — matches the lucide icons used
// in the filter chips. Stroke-based, so all paths share stroke + fill="none".
const SECTOR_SVG_INNER: Record<Sector, string> = {
  ALL:       `<rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>`,
  REFINERY:  `<path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z"/>`,
  PETROCHEM: `<path d="M10 2v7.5a2 2 0 0 1-.2.9L4.7 20.5a1 1 0 0 0 .9 1.5h12.8a1 1 0 0 0 .9-1.5l-5.1-10.1a2 2 0 0 1-.2-.9V2"/><path d="M8.5 2h7"/><path d="M7 16h10"/>`,
  GAS_PLANT: `<path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.07-2.14-.22-4.05 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.15.43-2.29 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>`,
  POWER:     `<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>`,
  OFFSHORE:  `<path d="M12 10.2V14"/><path d="M12 2v3"/><path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6"/><path d="M19.4 20A11.6 11.6 0 0 0 21 14l-8.2-3.64a2 2 0 0 0-1.6 0L3 14a11.6 11.6 0 0 0 2.8 7.76"/>`,
  CEMENT:    `<path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/>`,
  LNG:       `<line x1="2" x2="22" y1="12" y2="12"/><line x1="12" x2="12" y1="2" y2="22"/><path d="m20 16-4-4 4-4"/><path d="m4 8 4 4-4 4"/><path d="m16 4-4 4-4-4"/><path d="m8 20 4-4 4 4"/>`,
  STEEL:     `<path d="m15 12-8.37 8.37a1 1 0 1 1-3-3L12 9"/><path d="m18 15 4-4"/><path d="m21.5 11.5-1.9-1.9A2 2 0 0 1 19 8.17V7l-2.26-2.26a6 6 0 0 0-4.2-1.76L9 2.96l.92.82A6.18 6.18 0 0 1 12 8.4V10l2 2h1.17a2 2 0 0 1 1.4.59L18.5 14.5"/>`,
  TANK_FARM: `<ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5V19A9 3 0 0 0 21 19V5"/><path d="M3 12A9 3 0 0 0 21 12"/>`,
  OTHER:     `<circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/>`,
};

// Integrity "criticals" for a plant = thickness breaches + triaged crit/warn
// findings. (Raw open_findings is excluded — it's dominated by imported
// info-level items and would read as alarming noise.)
function criticals(p: Plant): number {
  return (p.cmls_below_min || 0) + (p.crit_findings || 0);
}

// 3-tier integrity health, one source of truth for chip + card + block + badge:
//   RED   "Action needed" — a CML is below t_min (a measured thickness breach)
//   AMBER "Watch"         — a triaged critical finding, but no thickness breach yet
//   GREEN "Healthy"       — has CMLs, no breach and no critical finding
//   GREY  "No data"       — no CMLs to judge from
type HealthTier = "crit" | "watch" | "ok" | "none";
function healthTier(p: Plant): HealthTier {
  if ((p.cmls_below_min || 0) > 0) return "crit";
  if ((p.crit_findings || 0) > 0) return "watch";
  if ((p.cml_count || 0) > 0) return "ok";
  return "none";
}
const TIER_META: Record<HealthTier, { color: string; label: string }> = {
  crit:  { color: "#C0392B", label: "Action needed" },
  watch: { color: "#E0A500", label: "Watch" },
  ok:    { color: "#1FA85C", label: "Healthy" },
  none:  { color: "#6E7585", label: "No data" },
};
const TIER_RANK: Record<HealthTier, number> = { crit: 3, watch: 2, ok: 1, none: 0 };
// Worst tier across a field's platforms — the field/block takes its governing one.
function fieldTier(plants: Plant[]): HealthTier {
  return plants.reduce<HealthTier>(
    (worst, p) => (TIER_RANK[healthTier(p)] > TIER_RANK[worst] ? healthTier(p) : worst), "none");
}

function classifyPlant(p: Plant): Sector {
  const cn = (p.client_name || "").toLowerCase();
  const pn = (p.name || "").toLowerCase();
  const pc = (p.code || "").toLowerCase();
  const loc = (p.location || "").toLowerCase();

  if (cn.includes("lng") || pn.includes("lng"))              return "LNG";
  // Industrial-gas / HyCO (hydrogen–CO syngas) producers — ONSHORE process
  // plants (Air Liquide / Linde etc.). Checked before OFFSHORE so a HyCO unit
  // never falls through to OTHER or gets mistaken for a platform.
  if (cn.includes("air liquide") || cn.includes("industrial gas")
      || cn.includes("linde") || cn.includes("praxair")
      || /\bhyco\b/.test(pn) || pc.startsWith("hyco"))         return "GAS_PLANT";
  // Offshore: explicit "offshore", a Gulf-of-Thailand field operator (e.g.
  // Valeura), an E&P/exploration company, or a plant literally named "Platform …".
  if (loc.includes("offshore") || cn.includes("gulf of thailand")
      || cn.includes("exploration") || /\bplatform\b/.test(pn)) return "OFFSHORE";
  if (cn.includes("cement") || pn.includes("cement"))        return "CEMENT";
  if (cn.includes("steel") || pn.includes("steel"))          return "STEEL";
  if (cn.includes("egat") || cn.includes("power")
      || pn.includes("power") || cn.includes("blcp")
      || cn.includes("ratch") || cn.includes("gpsc")
      || cn.includes("gulf energy") || cn.includes("gulf development")
      || cn.includes("hin krut"))                            return "POWER";
  if (cn.includes("aviation") || cn.includes("bafs"))        return "TANK_FARM";
  if (pn.includes("refinery") ||
      ["sr", "ry", "bcp", "src-esso", "mtp-sprc", "mtp-ref"].includes(pc))
                                                              return "REFINERY";
  if (pc.startsWith("gsp") || pc === "esp" || pc === "knm")  return "GAS_PLANT";
  if (cn.includes("ptt") && !cn.includes("exploration"))     return "GAS_PLANT";
  if (cn.includes("chemical") || cn.includes("pttgc")
      || cn.includes("scgc") || cn.includes("indorama")
      || cn.includes("basf") || cn.includes("hmc")
      || cn.includes("agc") || cn.includes("eastman")
      || cn.includes("dow"))                                  return "PETROCHEM";
  return "OTHER";
}

// ── Thailand bounds — covers all typical XZYMODS customer sites ──
const TH_BOUNDS: LatLngBoundsExpression = [
  [5.5, 97.0],    // SW (Phuket area)
  [20.5, 105.6],  // NE (Chiang Rai)
];

// ── Marker icon — white circle with sector icon + code label ──
function makeMarkerIcon(plant: Plant, isActive: boolean): L.DivIcon {
  const sector = classifyPlant(plant);
  const meta = SECTOR_META[sector];
  const sectorColor = meta.color;
  // Health overrides the ring: a plant with thickness breaches / crit findings
  // reads red on the map (the locator becomes an integrity signal). Detail/text
  // lives in the card list — the map dot is the at-a-glance.
  const critN = criticals(plant);
  const discColor = critN > 0 ? "#C0392B" : sectorColor;
  const logoUrl = plant.client_id != null ? CLIENT_LOGOS[plant.client_id] : undefined;
  const discFill = logoUrl ? "#FFFFFF" : discColor;
  const discStroke = critN > 0 ? "#C0392B" : (logoUrl ? sectorColor : "#FBFAF5");
  const r = isActive ? 11 : 9;                  // a bit larger to fit icon
  const stroke = critN > 0 ? (isActive ? 3 : 2.4) : (isActive ? 2.5 : 2);

  const code = escapeXml(plant.code || "");
  const fontSize = isActive ? 11 : 10;
  // JetBrains Mono ≈ 0.60em per glyph + 0.06em letter-spacing — round up to 0.72
  // to leave room for the 3px paint-order stroke around each glyph.
  const labelW = Math.max(code.length * fontSize * 0.72, 30);
  const gap = 4;
  const padHalo = isActive ? r + 10 : r + 5;

  const svgW = padHalo + r + gap + labelW + 12;
  const svgH = (r + padHalo) * 2;
  const cx = padHalo + r;
  const cy = svgH / 2;
  const labelX = cx + r + gap;

  const halo = isActive
    ? `<circle cx="${cx}" cy="${cy}" r="${r + 8}" fill="${sectorColor}" opacity="0.14" />`
    : "";

  // Sector icon — lucide 24×24 path, scaled to fit ~70% of marker diameter
  const iconPaths = SECTOR_SVG_INNER[sector] || SECTOR_SVG_INNER.OTHER;
  const iconScale = (r * 2 * 0.70) / 24;
  const iconStroke = 2.6 / iconScale;            // counter-scale so stroke looks 2px
  const labelColor = "#1F4E79";                  // code label stays navy for legibility

  // Critical badge — red disc + "!" at top-right (colour + symbol, not colour alone)
  const bx = cx + r * 0.82;
  const by = cy - r * 0.82;
  const br = isActive ? 6.5 : 5.5;
  const badge = critN > 0
    ? `<circle cx="${bx}" cy="${by}" r="${br}" fill="#C0392B" stroke="#FBFAF5" stroke-width="1.4" />
       <text x="${bx}" y="${by + br * 0.62}" text-anchor="middle"
             font-family="JetBrains Mono, monospace" font-size="${br * 1.5}" font-weight="800"
             fill="#FFFFFF">!</text>`
    : "";

  const critPing = critN > 0 ? `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="#C0392B" stroke-width="1.5" opacity="0.5"><animate attributeName="r" values="${r};${(r * 2.1).toFixed(1)}" dur="2.2s" repeatCount="indefinite"/><animate attributeName="opacity" values="0.5;0" dur="2.2s" repeatCount="indefinite"/></circle>` : "";
  const svg = `<svg width="${svgW}" height="${svgH}" viewBox="0 0 ${svgW} ${svgH}" xmlns="http://www.w3.org/2000/svg">
    ${halo}
    ${critPing}
    <circle cx="${cx}" cy="${cy + 1.2}" r="${r}" fill="rgba(20,38,57,0.22)" />
    <circle cx="${cx}" cy="${cy}" r="${r}" fill="${discFill}" stroke="${discStroke}" stroke-width="${stroke}" />
    ${logoUrl ? `<image href="${logoUrl}" x="${cx - r * 0.82}" y="${cy - r * 0.82}" width="${(r * 1.64).toFixed(1)}" height="${(r * 1.64).toFixed(1)}" preserveAspectRatio="xMidYMid meet" />` : ""}
    <g transform="translate(${cx} ${cy}) scale(${iconScale}) translate(-12 -12)"
       fill="none" stroke="#FFFFFF" stroke-width="${iconStroke.toFixed(2)}"
       stroke-linecap="round" stroke-linejoin="round">
      ${logoUrl ? "" : iconPaths}
    </g>
    ${badge}
    <text x="${labelX}" y="${cy + fontSize * 0.35}"
          font-family="JetBrains Mono, monospace"
          font-size="${fontSize}" font-weight="700"
          letter-spacing="0.06em" fill="${labelColor}"
          style="paint-order:stroke;stroke:rgba(251,250,245,0.95);stroke-width:3px;stroke-linejoin:round;">${code}</text>
  </svg>`;

  return L.divIcon({
    html: svg,
    className: "xzynexa-marker",
    iconSize: [svgW, svgH],
    iconAnchor: [cx, cy],
  });
}

function escapeXml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Cluster icon — navy/amber by count, but RED when any plant inside has a
// critical (the child marker's icon carries the red badge colour, so we can
// detect it without re-plumbing the plant data through the cluster).
function createClusterIcon(cluster: {
  getChildCount: () => number;
  getAllChildMarkers?: () => Array<{ options?: { icon?: { options?: { html?: string } } } }>;
}): L.DivIcon {
  const n = cluster.getChildCount();
  const children = cluster.getAllChildMarkers?.() ?? [];
  const hasCrit = children.some((m) => {
    const html = m.options?.icon?.options?.html;
    return typeof html === "string" && html.includes("#C0392B");
  });
  const tier = n < 5 ? "" : n < 15 ? "is-md" : "is-lg";
  const size = n < 5 ? 36 : n < 15 ? 42 : 50;
  const critStyle = hasCrit
    ? "background:#C0392B;border-color:#FBFAF5;color:#FFFFFF;"
    : "";
  return L.divIcon({
    html: `<div class="xzynexa-cluster-inner ${tier}${hasCrit ? " is-crit" : ""}" style="width:${size}px;height:${size}px;${critStyle}">${n}</div>`,
    className: "xzynexa-cluster",
    iconSize: L.point(size, size, true),
  });
}

interface MarkerScreenPos { x: number; y: number; }

export default function AtlasScreen(): JSX.Element {
  const navigate = useNavigate();
  const [activePlantId, setActivePlantId] = useState<number | null>(null);
  const [markerScreenPos, setMarkerScreenPos] = useState<Record<number, MarkerScreenPos>>({});
  const [filter, setFilter] = useState("");
  const [sortBy, setSortBy] = useState<"risk" | "name">("risk");
  const [showBlocks] = useState(true);
  const [hoveredCode, setHoveredCode] = useState<string | null>(null);
  const cardRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const containerRef = useRef<HTMLDivElement>(null);

  const plantsQ = useQuery({
    queryKey: ["plants", { active_only: true }],
    queryFn: () => apiClient.plants({ active_only: true }),
    // Plant roster changes rarely; keep it fresh for 5 min and serve the cached
    // copy on re-navigation so the atlas is instant after the first load
    // (the list endpoint is a remote round-trip — see the backend batch fix).
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
  const allPlants = plantsQ.data ?? [];

  // Pipeline routes — straight geographic lines between this tenant's plant
  // nodes (empty unless the client has a defined network, e.g. East Water).
  const networkClientId = allPlants[0]?.client_id;
  const networkQ = useQuery({
    queryKey: ["pipeline-network", networkClientId],
    queryFn: () => apiClient.pipelineNetwork(networkClientId!),
    enabled: networkClientId != null,
  });
  const pipelineSegments = networkQ.data?.segments ?? [];

  // Classify every plant once for filter + per-card badge
  const classified = useMemo(
    () => allPlants.map((p) => ({ plant: p, sector: classifyPlant(p) })),
    [allPlants],
  );

  // Filter chain — search → sort
  const plants = useMemo(() => {
    const pool = classified;
    const q = filter.trim().toLowerCase();
    const arr = q
      ? pool.filter(({ plant: p }) =>
          (p.code || "").toLowerCase().includes(q) ||
          (p.name || "").toLowerCase().includes(q) ||
          (p.location || "").toLowerCase().includes(q) ||
          (p.client_name || "").toLowerCase().includes(q),
        )
      : pool;
    const out = arr.map((x) => x.plant);
    // Triage default: worst-first (below-min, then crit findings, then size),
    // so the plant that needs attention sits at the top of the scroll.
    out.sort((a, b) =>
      sortBy === "name"
        ? (a.name || "").localeCompare(b.name || "")
        : criticals(b) - criticals(a)
          || b.crit_findings - a.crit_findings
          || b.asset_count - a.asset_count
          || (a.name || "").localeCompare(b.name || ""),
    );
    return out;
  }, [classified, filter, sortBy]);

  // Only plants with coords go on the map; the rest still appear as cards.
  const mapPlants = useMemo(() => plants.filter((p) => p.lat != null && p.lon != null), [plants]);

  const activeBlockCode = activePlantId != null
    ? blockCodeFromLocation(plants.find((p) => p.id === activePlantId)?.location ?? "")
    : null;

  // Field-schematic panel: opens when a cluster of CO-LOCATED platforms (one
  // shared field coordinate) is clicked — they can't be split on the map by
  // zoom, so we list them in an illustrative panel instead.
  const [fieldPanel, setFieldPanel] = useState<{ field: string; plants: Plant[]; offshore: boolean } | null>(null);

  // Offshore fields are co-located: every platform in a field shares the block's
  // coordinate, so the BLOCK is the map target (no stacking pins). Group plants
  // by block; a plant NOT in a tenant block keeps a normal marker.
  const blockFields = useMemo(() => {
    const m = new Map<string, Plant[]>();
    for (const p of mapPlants) {
      const c = blockCodeFromLocation(p.location ?? "");
      if (!c) continue;
      const arr = m.get(c);
      if (arr) arr.push(p); else m.set(c, [p]);
    }
    return m;
  }, [mapPlants]);
  const tenantCodes = useMemo(
    () => new Set(CONCESSION_BLOCKS.filter((b) => b.tenant).map((b) => b.code)), []);
  const unblockedPlants = useMemo(
    () => mapPlants.filter((p) => {
      const c = blockCodeFromLocation(p.location ?? "");
      return !(c && tenantCodes.has(c));
    }), [mapPlants, tenantCodes]);
  const openField = useCallback((code: string) => {
    const fps = blockFields.get(code);
    if (fps && fps.length) {
      setFieldPanel({ field: fps[0].location || "Field", plants: fps, offshore: true });
    }
  }, [blockFields]);
  // One badge per tenant field — operator logo + block code + platform count —
  // placed at the field's shared coordinate, so the tenant's blocks read as theirs.
  const tenantFields = useMemo(() => {
    const out: { code: string; plant: Plant; count: number; tier: HealthTier; logoUrl?: string }[] = [];
    for (const [code, fps] of blockFields) {
      if (!tenantCodes.has(code) || fps.length === 0) continue;
      const p0 = fps[0];
      out.push({
        code, plant: p0, count: fps.length,
        tier: fieldTier(fps),
        logoUrl: p0.client_id != null ? CLIENT_LOGOS[p0.client_id] : undefined,
      });
    }
    return out;
  }, [blockFields, tenantCodes]);
  // Stable ref so the once-bound cluster handler always reads current plants.
  const plantsRef = useRef<Plant[]>(mapPlants);
  plantsRef.current = mapPlants;
  // Callback-ref → state: react-leaflet-cluster attaches the group ref AFTER the
  // parent's first effect, so a plain useRef is null at bind time. A state ref
  // re-runs the effect once the real group is attached.
  const [clusterGroup, setClusterGroup] = useState<L.MarkerClusterGroup | null>(null);

  // Stable handler (reads plantsRef + stable setFieldPanel) so the bind/unbind
  // in the effect below use the SAME reference — clean removal on remount.
  const handleClusterClick = useCallback((e: L.LeafletEvent): void => {
    const cluster = (e as unknown as { layer: {
      getLatLng: () => L.LatLng;
      zoomToBounds: (o?: L.FitBoundsOptions) => void;
    } }).layer;
    // A pure co-located cluster sits exactly on one shared coordinate, so its
    // centroid matches the plants there. A mixed cluster's centroid matches no
    // plant → `here` is empty → we zoom in to separate the fields instead.
    const center = cluster.getLatLng();
    const here = plantsRef.current.filter(
      (p) => p.lat != null && p.lon != null
        && Math.abs(Number(p.lat) - center.lat) < 1e-5
        && Math.abs(Number(p.lon) - center.lng) < 1e-5,
    );
    if (here.length > 1) {
      setFieldPanel({
        field: here[0].location || "Field",
        plants: here,
        offshore: classifyPlant(here[0]) === "OFFSHORE",
      });
    } else {
      cluster.zoomToBounds({ padding: [40, 40] });
    }
  }, []);

  useEffect(() => {
    if (!clusterGroup) return;
    clusterGroup.on("clusterclick", handleClusterClick);
    return () => { clusterGroup.off("clusterclick", handleClusterClick); };
  }, [clusterGroup, handleClusterClick]);

  // KPIs reconcile with the current filter (the old build mixed a filtered
  // PLANTS count with unfiltered AREAS/ASSETS). Per-plant criticals still
  // surface on the cards + map; the header strip stays summary-only.
  const visAssets = plants.reduce((s, p) => s + p.asset_count, 0);
  // Exception-first: how many visible plants need an engineer's attention (any
  // thickness breach or triaged crit finding). Surfaced as the lead KPI so a new
  // engineer sees WHERE to look before scanning the whole list.
  const attentionCount = plants.filter((p) => criticals(p) > 0).length;

  // Connector line (active card ↔ active marker on map)
  const connector = (() => {
    if (!activePlantId || !containerRef.current) return null;
    const cardEl = cardRefs.current[activePlantId];
    const marker = markerScreenPos[activePlantId];
    if (!cardEl || !marker) return null;
    const cardRect = cardEl.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    return {
      x1: cardRect.right - containerRect.left,
      y1: cardRect.top + cardRect.height / 2 - containerRect.top,
      x2: marker.x,
      y2: marker.y,
    };
  })();

  return (
    <div className="absolute inset-0 overflow-hidden" ref={containerRef}>
      {/* ── Map (full bleed) ────────────────────────────────────── */}
      <MapContainer
        bounds={TH_BOUNDS}
        boundsOptions={{ paddingTopLeft: [380, 130], paddingBottomRight: [60, 60] }}
        className="absolute inset-0 h-full w-full"
        style={{ background: "#FBFAF5" }}
        zoomControl={false}
        scrollWheelZoom={true}
        // Offshore assets sit in open sea — deep zoom shows nothing and hammers
        // the basemap CDN (CARTO rate-limits → blank tiles). Cap it; co-located
        // platforms are reached via the field-schematic panel, not by zooming.
        minZoom={5}
        maxZoom={11}
      >
        {/* OpenStreetMap, not CARTO voyager. CARTO now requires an API key on
            that endpoint, and without one every tile renders its own "API KEY
            REQUIRED" watermark -- which is what production was serving, seen by
            eye on 2026-09-01 and by nothing else: the tiles load with HTTP 200,
            so no console error, no failed request, and no test anywhere renders
            a real basemap. OSM needs no key. Its plate is flatter than
            voyager's, which the plot overlays sit on more quietly anyway. */}
        <TileLayer
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          maxZoom={11}
        />
        {/* Raw-water pipeline routes — drawn under the markers. */}
        {pipelineSegments.map((s, i) => (
          <Polyline
            key={`seg-${i}`}
            positions={[[s.from.lat, s.from.lon], [s.to.lat, s.to.lon]]}
            pathOptions={{
              color: s.kind === "backup" ? "#94A3B8" : "#2563EB",
              weight: s.kind === "main" ? 3 : 2,
              opacity: 0.85,
              dashArray: s.kind === "backup" ? "6 6" : undefined,
            }}
          >
            <Tooltip sticky>{s.label}</Tooltip>
          </Polyline>
        ))}
        {showBlocks && (
          <ConcessionBlocksLayer
            activeCode={activeBlockCode}
            hoveredCode={hoveredCode}
            blockFields={blockFields}
            onFieldOpen={openField}
            onFieldHover={setActivePlantId}
            onBlockHover={setHoveredCode}
          />
        )}
        <MarkerClusterGroup
          ref={setClusterGroup}
          chunkedLoading
          // Tight radius so distinct fields (tens of km apart) don't merge at the
          // overview — only TRULY co-located platforms cluster. Lets a field
          // cluster open its panel in one click instead of zoom-then-click.
          maxClusterRadius={14}
          zoomToBoundsOnClick={false}
          showCoverageOnHover={false}
          iconCreateFunction={createClusterIcon as unknown as (cluster: L.MarkerCluster) => L.DivIcon}
        >
          {unblockedPlants.map((plant) => (
            <Marker
              key={plant.id}
              position={[Number(plant.lat), Number(plant.lon)]}
              icon={makeMarkerIcon(plant, activePlantId === plant.id)}
              eventHandlers={{
                mouseover: () => setActivePlantId(plant.id),
                mouseout:  () => setActivePlantId(null),
                click:     () => navigate(`/sites/${plant.id}`),
              }}
            />
          ))}
        </MarkerClusterGroup>
        {/* Tenant field badges — operator logo + code + count, on each tenant block */}
        {showBlocks && tenantFields.map((f) => (
          <Marker
            key={`badge-${f.code}`}
            position={[Number(f.plant.lat), Number(f.plant.lon)]}
            icon={makeFieldBadge(f.code, f.count, f.tier, f.logoUrl)}
            eventHandlers={{
              click: () => openField(f.code),
              // The badge covers most of a small block, so the polygon's own
              // mouseover rarely fires — drive the highlight from the badge directly.
              mouseover: () => { setHoveredCode(f.code); setActivePlantId(f.plant.id); },
              mouseout: () => { setHoveredCode(null); setActivePlantId(null); },
            }}
          />
        ))}
        <FitToPlants plants={mapPlants} fitKey={networkClientId} />
        <MapInteractionLayer plants={mapPlants} onMarkerScreenPos={setMarkerScreenPos} />
        <MapAutoResize />
      </MapContainer>

      {/* ── Floating header ─────────────────────────────────────── */}
      <header
        className="absolute top-0 left-0 right-0 px-8 py-4 flex items-end justify-between gap-8 pointer-events-none"
        style={{ zIndex: 1000 }}
      >
        <div className="min-w-0 pointer-events-auto">
          <div className="font-mono uppercase mb-1"
               style={{
                 fontSize: 10.5, letterSpacing: ".24em",
                 color: "var(--accent-deep)", fontWeight: 700,
                 textShadow: "0 1px 2px rgba(251,250,245,1), 0 0 10px rgba(251,250,245,0.9)",
               }}>
            / ASSET ATLAS
          </div>
          <h1 className="font-display font-bold tracking-tight leading-none m-0"
              style={{
                fontSize: 28, color: "var(--ink-1)", letterSpacing: "-.015em",
                textShadow: "0 1px 2px rgba(251,250,245,1), 0 0 14px rgba(251,250,245,1), 0 0 24px rgba(251,250,245,0.7)",
              }}>
            Plant Selector
          </h1>
          <p className="mt-1.5 max-w-xl m-0"
             style={{
               fontSize: 12, color: "var(--ink-2)", lineHeight: 1.5, fontWeight: 500,
               textShadow: "0 1px 2px rgba(251,250,245,1), 0 0 8px rgba(251,250,245,0.95)",
             }}>
            Select a plant to view areas, assets, and inspection history.
          </p>
        </div>

        <div className="grid grid-cols-3 shrink-0 pointer-events-auto overflow-hidden"
             style={{
               background: "rgba(255,255,255,0.72)",
               backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
               border: "1px solid var(--hairline)", borderRadius: 8,
               boxShadow: "0 8px 28px rgba(10,22,40,0.12), 0 1px 0 rgba(255,255,255,0.75) inset",
             }}>
          <KpiCell label="NEEDS ATTN" value={attentionCount} crit />
          <KpiCell label="PLANTS" value={plants.length} divider />
          <KpiCell label="ASSETS" value={visAssets} divider />
        </div>
      </header>

      {/* ── Cards overlay (left) ─────────────────────────────────── */}
      <aside
        className="absolute left-6 w-[380px] flex flex-col gap-2"
        style={{ top: "112px", bottom: "24px", zIndex: 900 }}
      >
        {/* Sticky filter bar */}
        <div className="shrink-0 flex flex-col overflow-hidden"
             style={{
               background: "rgba(255,255,255,0.80)",
               backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
               border: "1px solid var(--hairline)", borderRadius: 8,
               boxShadow: "0 8px 24px rgba(10,22,40,0.10)",
             }}>

          {/* Search row */}
          <div className="flex items-center gap-2 px-3 py-2.5">
            <Search size={13} style={{ color: "var(--ink-4)" }} className="shrink-0" />
            <input
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search all plants…"
              className="flex-1 outline-none bg-transparent font-mono"
              style={{ fontSize: 12, color: "var(--ink-1)", letterSpacing: "0.02em" }}
            />
            {filter && (
              <button onClick={() => setFilter("")}
                      aria-label="Clear search"
                      className="shrink-0 p-0.5 hover:text-[var(--accent)]"
                      style={{ color: "var(--ink-3)" }}>
                <X size={13} />
              </button>
            )}
            <button
              onClick={() => setSortBy((s) => (s === "risk" ? "name" : "risk"))}
              title={sortBy === "risk" ? "Sorted by risk (worst first) — click for A–Z" : "Sorted A–Z — click for risk"}
              className="shrink-0 font-mono uppercase px-1.5 py-0.5 border transition-all duration-150"
              style={{ fontSize: 9, letterSpacing: ".1em",
                       background: sortBy === "risk" ? "rgba(var(--accent-rgb) / 0.08)" : "transparent",
                       borderColor: sortBy === "risk" ? "rgba(var(--accent-rgb) / 0.4)" : "var(--hairline)",
                       color: sortBy === "risk" ? "var(--accent-deep)" : "var(--ink-3)" }}
            >
              {sortBy === "risk" ? "RISK ↓" : "A–Z"}
            </button>
            <span className="font-mono uppercase tabular-nums shrink-0"
                  style={{ fontSize: 10, letterSpacing: "0.18em", color: "var(--ink-4)" }}>
              {filter
                ? `${plants.length}/${allPlants.length}`
                : allPlants.length}
            </span>
          </div>
        </div>

        {/* Cards scroll area */}
        <div className="flex-1 flex flex-col gap-2 overflow-y-auto atlas-cards-scroll">
        {plantsQ.isPending && (
          <div className="panel-raised p-4 text-center text-ink-3 font-mono text-xs">
            Loading plants…
          </div>
        )}
        {plantsQ.isError && (
          <div className="panel-raised p-4 text-status-crit font-mono text-xs">
            Failed to load plants: {(plantsQ.error as Error).message}
          </div>
        )}
        {plantsQ.isSuccess && plants.length === 0 && (
          <div className="panel-raised p-4 text-center text-ink-3 font-mono text-xs">
            {filter
              ? `No plants match "${filter}". Try a different keyword.`
              : "No plants visible. Contact XZYMODS support."}
          </div>
        )}
        {plants.map((plant, idx) => (
          <PlantCard
            key={plant.id}
            plant={plant}
            isActive={activePlantId === plant.id}
            animDelay={Math.min(idx, 8) * 0.04}
            onMouseEnter={() => setActivePlantId(plant.id)}
            onMouseLeave={() => setActivePlantId(null)}
            cardRef={(el) => { cardRefs.current[plant.id] = el; }}
          />
        ))}
        </div>
      </aside>

      {/* ── Connector line ─────────────────────────────────────── */}
      {connector && (
        <svg
          className="absolute inset-0 pointer-events-none"
          style={{ zIndex: 750 }}
        >
          <line x1={connector.x1} y1={connector.y1} x2={connector.x2} y2={connector.y2}
                stroke="#FBFAF5" strokeWidth="4" opacity="0.7" />
          <line x1={connector.x1} y1={connector.y1} x2={connector.x2} y2={connector.y2}
                stroke="#1F4E79" strokeWidth="1.2" strokeDasharray="5 3" />
          <circle cx={connector.x1} cy={connector.y1} r="4" fill="#FBFAF5" stroke="#1F4E79" strokeWidth="1.2" />
          <circle cx={connector.x2} cy={connector.y2} r="3" fill="#1F4E79" />
        </svg>
      )}

      {/* ── Field schematic panel (co-located cluster) ───────────── */}
      {fieldPanel && (
        <FieldSchematicPanel
          field={fieldPanel.field}
          plants={fieldPanel.plants}
          offshore={fieldPanel.offshore}
          onClose={() => setFieldPanel(null)}
        />
      )}

    </div>
  );
}

// ── KPI cell (top-right strip) ─────────────────────────────────────
function KpiCell({ label, value, crit, divider }: { label: string; value: number; crit?: boolean; divider?: boolean }): JSX.Element {
  const hot = crit && value > 0;
  return (
    <div className="px-4 py-2.5 min-w-[108px]"
         style={divider ? { borderLeft: "1px solid var(--hairline)" } : undefined}>
      <div className="label-eng flex items-center gap-1" style={{ color: "var(--ink-3)" }}>
        {label}
        {hot && <AlertTriangle size={9} className="text-status-crit" />}
      </div>
      <div className="font-display font-bold tabular leading-none mt-1"
           style={{ fontSize: 22, letterSpacing: "-.01em", color: hot ? "#C0392B" : "var(--ink-1)" }}>
        {value.toLocaleString()}
      </div>
    </div>
  );
}

// ── Plant health verdict — the "senior read" surfaced for engineers who don't
// yet have the gut-feel: one plain-language status, never a raw count to decode.
// Honest 3-state — a plant with no CMLs reads "No data", never a false "Healthy".
function plantHealth(p: Plant): { label: string; color: string; icon: typeof AlertTriangle } {
  const t = healthTier(p);
  const icon = t === "ok" ? CheckCircle2 : t === "none" ? MinusCircle : AlertTriangle;
  return { label: TIER_META[t].label, color: TIER_META[t].color, icon };
}

function HealthChip({ plant }: { plant: Plant }): JSX.Element {
  const h = plantHealth(plant);
  const Icon = h.icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 border font-mono text-[9px] uppercase tracking-wide whitespace-nowrap"
      style={{ color: h.color, borderColor: h.color + "59", background: h.color + "14", borderRadius: 5 }}
    >
      <Icon size={9} className="shrink-0" /> {h.label}
    </span>
  );
}

// ── Operator monogram — a deterministic logo tile (initials in the sector
// colour) so each card carries a recognisable identity without a logo asset.
function operatorInitials(p: Plant): string {
  const src = (p.client_name || p.name || p.code || "?").replace(/\(.*?\)/g, " ").trim();
  const words = src.split(/\s+/).filter(Boolean);
  const raw = words.length >= 2 ? words[0][0] + words[1][0] : src.slice(0, 2);
  return raw.toUpperCase().slice(0, 2);
}

function OperatorMonogram({ plant }: { plant: Plant }): JSX.Element {
  const color = SECTOR_META[classifyPlant(plant)].color;
  return (
    <div
      className="shrink-0 flex items-center justify-center font-display font-bold select-none"
      style={{ width: 34, height: 34, color, border: "1.5px solid " + color, background: color + "14", fontSize: 12.5 }}
      aria-hidden
    >
      {operatorInitials(plant)}
    </div>
  );
}

// Client brand logos: single source of truth in lib/clientLogos.ts (imported
// above) — shared by the Leaflet marker, the Atlas cards, and the field
// schematic. Add a customer there + drop the file in public/client-logos/.

function ClientLogo({ plant }: { plant: Plant }): JSX.Element {
  const src = plant.client_id != null ? CLIENT_LOGOS[plant.client_id] : undefined;
  if (!src) return <OperatorMonogram plant={plant} />;
  return (
    <img
      src={src}
      alt={(plant.client_name || "") + " logo"}
      className="shrink-0 object-contain"
      style={{ width: 34, height: 34 }}
    />
  );
}

// ── Plant card (left overlay) ──────────────────────────────────────
interface CardProps {
  plant: Plant;
  isActive: boolean;
  animDelay: number;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  cardRef: (el: HTMLDivElement | null) => void;
}
function PlantCard({ plant, isActive, animDelay, onMouseEnter, onMouseLeave, cardRef }: CardProps): JSX.Element {
  const navigate = useNavigate();
  const tier = healthTier(plant);
  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: animDelay }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="shrink-0"
      ref={cardRef}
    >
      <button
        onClick={() => navigate(`/sites/${plant.id}`)}
        className={`block w-full text-left relative overflow-hidden panel-raised transition-all group tier-${tier} ${
          isActive ? "border-brand shadow-glow" : "hover:border-brand/60"
        }`}
      >
        {/* Header */}
        <div className="px-3 py-2 flex items-start justify-between gap-2">
          <div className="flex items-start gap-2.5 min-w-0 flex-1">
            <ClientLogo plant={plant} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[11px] uppercase tracking-wider text-brand">
                  {plant.code}
                </span>
                {plant.installation_year && (
                  <span className="font-mono text-[9px] text-ink-3">
                    / {plant.installation_year}
                  </span>
                )}
              </div>
              <h2 className="font-display font-bold text-base text-ink-1 mt-0.5 leading-tight truncate">
                {plant.name}
              </h2>
              <div className="text-[10px] text-ink-3 mt-0.5 truncate">
                {plant.location || plant.client_name}
              </div>
              {plant.lat != null && plant.lon != null && (
                <div className="mt-1 flex items-center gap-1.5 font-mono text-[9px] text-ink-3">
                  <MapPin size={8} className="text-brand/70 shrink-0" />
                  <span className="truncate">
                    {Number(plant.lat).toFixed(3)}°N {Number(plant.lon).toFixed(3)}°E
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0 mt-0.5">
            <HealthChip plant={plant} />
            <ArrowUpRight size={14}
              className="text-ink-3 group-hover:text-brand group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
          </div>
        </div>

        {/* Stats strip — CRIT is the real integrity exception (was hardcoded 0) */}
        <div className="grid grid-cols-3 gap-px bg-panel-700 border-y border-panel-700">
          <Stat label="ASSETS"   value={plant.asset_count} />
          <Stat label="CMLs"     value={plant.cml_count} />
          <Stat label="CMLs<min" value={plant.cmls_below_min || 0} crit />
        </div>

        {/* Footer */}
        <div className="px-3 py-1.5 bg-panel-950 flex items-center justify-between font-mono text-[9px] uppercase tracking-wider">
          <span className="text-ink-3 flex items-center gap-1.5 truncate">
            <Anchor size={8} className="text-status-ok shrink-0" />
            {plant.client_name}
          </span>
          <span className="text-brand whitespace-nowrap">OPEN →</span>
        </div>
      </button>
    </motion.div>
  );
}

function Stat({ label, value, crit }: { label: string; value: number; crit?: boolean }): JSX.Element {
  return (
    <div className="bg-panel-850 px-3 py-1">
      <div className="label-eng text-[9px] flex items-center gap-1">
        {label}
        {crit && value > 0 && <AlertTriangle size={8} className="text-status-crit" />}
      </div>
      <div className={`font-mono text-sm tabular leading-tight ${
        crit && value > 0 ? "text-status-crit font-bold" : "text-ink-1"
      }`}>
        {value}
      </div>
    </div>
  );
}

// ── Field badge — a small white pill on each tenant block: operator logo +
// block code + platform count. Its border + count chip take the field's health
// colour (same TIER_META palette as the card and block fill); "!" only for crit.
function makeFieldBadge(code: string, count: number, tier: HealthTier, logoUrl?: string): L.DivIcon {
  const color = TIER_META[tier].color;
  const bang = tier === "crit";
  const logo = logoUrl
    ? `<img class="field-badge-logo" src="${logoUrl}" alt="" />`
    : `<span class="field-badge-logo field-badge-logo--mono">${escapeXml(code.slice(0, 2))}</span>`;
  const w = 38 + escapeXml(code).length * 7.5 + (bang ? 30 : 24);
  const html = `<div class="field-badge" style="border-color:${color}">`
    + logo
    + `<span class="field-badge-code">${escapeXml(code)}</span>`
    + `<span class="field-badge-count" style="background:${color}">${bang ? "! " : ""}${count}</span>`
    + `</div>`;
  return L.divIcon({ html, className: "field-badge-wrap", iconSize: [w, 28], iconAnchor: [w / 2, 14] });
}

// Tenant blocks render last (on top) so they stay clickable above the context
// blocks; otherwise blocks keep their natural data order — the ODM 2019 set
// contains genuinely overlapping blocks (round-1 areas + 2018 PSC blocks over
// the older ones) and we show them all as-is.
const ORDERED_BLOCKS: ConcessionBlock[] = (() => {
  const arr = CONCESSION_BLOCKS.filter((b) => b.ring.length >= 3);
  return [...arr.filter((b) => !b.tenant), ...arr.filter((b) => b.tenant)];
})();

// ── Concession block overlay — the tenant's offshore fields ARE the map target.
// Every platform in a field shares one coordinate, so pins would stack invisibly;
// instead the BLOCK is clickable (→ opens the field's platform list) and carries
// the field's own exception state: fill + label turn RED when any platform needs
// attention, with the platform count and a "!" alongside (colour + icon + text =
// three channels). Non-tenant blocks are faint context that highlight amber and
// show their code on hover. `activeCode` is the block under the hovered card,
// `hoveredCode` the block under the cursor; either lights it up.
function ConcessionBlocksLayer({
  activeCode, hoveredCode, blockFields, onFieldOpen, onFieldHover, onBlockHover,
}: {
  activeCode: string | null;
  hoveredCode: string | null;
  blockFields: Map<string, Plant[]>;
  onFieldOpen: (code: string) => void;
  onFieldHover: (plantId: number | null) => void;
  onBlockHover: (code: string | null) => void;
}): JSX.Element {
  return (
    <>
      {ORDERED_BLOCKS.map((b, i) => {
        const active = b.code === activeCode || b.code === hoveredCode;
        // A block only counts as "the SELECTED tenant's" when the current tenant
        // actually has platforms in it (blockFields is built from the loaded plants),
        // NOT from the static `tenant` flag — otherwise Valeura's blocks would stay
        // filled after switching to another tenant.
        const fieldPlants = blockFields.get(b.code);
        const tier = fieldPlants ? fieldTier(fieldPlants) : "none";
        // Uniform grey stroke for EVERY block so the outlines read consistently;
        // meaning lives in the FILL. The selected tenant's block takes its field's
        // governing health colour (red / amber / green); every other block is
        // outline-only; the hovered block turns amber. Same TIER_META palette as
        // the cards and badges.
        const stroke = active ? "#C76B07" : "#C3C8D0";
        const fill = active ? "#C76B07" : fieldPlants ? TIER_META[tier].color : "#1F4E79";
        return (
          <Polygon
            key={`${b.code}-${i}`}
            positions={b.ring.map(([lon, lat]) => [lat, lon] as [number, number])}
            eventHandlers={{
              mouseover: (e) => {
                // Lift the hovered block above its neighbours so the amber
                // outline isn't overdrawn by later blocks' grey strokes.
                (e.target as L.Path).bringToFront();
                onBlockHover(b.code);
                if (fieldPlants?.[0]) onFieldHover(fieldPlants[0].id);
              },
              mouseout: () => { onBlockHover(null); onFieldHover(null); },
              ...(fieldPlants ? { click: () => onFieldOpen(b.code) } : {}),
            }}
            pathOptions={{
              color: stroke,
              weight: active ? 2.8 : 1.6,
              opacity: 1,
              fillColor: fill,
              // Context blocks stay OUTLINE-ONLY (the ODM set overlaps; stacked
              // fills make uneven patches). Only the selected tenant's blocks fill
              // — by tier — plus the hovered block, whose fill is opaque enough to
              // mask the grey strokes of overlapping blocks showing through.
              fillOpacity: active ? 0.6 : fieldPlants ? 0.3 : 0,
              className: fieldPlants ? "concession-clickable" : "",
            }}
          >
            {!fieldPlants ? (
              <Tooltip sticky className="concession-label is-context">
                {b.code}
              </Tooltip>
            ) : null}
          </Polygon>
        );
      })}
    </>
  );
}

// ── Fit the map to the whole concession-block layer (plus this tenant's assets)
// on first load, so every block is visible at a glance. The tenant's Gulf plants
// sit inside the frame; zoom into them for platform detail. Runs once; user
// panning is preserved.
// Reframe the map to the SELECTED tenant's footprint. Re-fits whenever `fitKey`
// (the tenant's client id) changes — i.e. on tenant switch — but not when a search
// filter merely narrows the list. Bounds = the tenant's plant coords plus the rings
// of any concession blocks they occupy, so whole fields frame cleanly.
// Leaflet caches its container size. When the container resizes — the MODS panel
// opening/closing, a window resize — it must re-measure or tiles won't fill the
// newly-exposed area (a blank strip). A ResizeObserver invalidates it on any change.
function MapAutoResize(): null {
  const map = useMap();
  useEffect(() => {
    const el = map.getContainer();
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(el);
    return () => ro.disconnect();
  }, [map]);
  return null;
}

function FitToPlants({ plants, fitKey }: { plants: Plant[]; fitKey: number | undefined }): null {
  const map = useMap();
  useEffect(() => {
    const pts: [number, number][] = [];
    const codes = new Set<string>();
    for (const p of plants) {
      if (p.lat != null && p.lon != null) pts.push([Number(p.lat), Number(p.lon)]);
      const c = blockCodeFromLocation(p.location ?? "");
      if (c) codes.add(c);
    }
    for (const b of CONCESSION_BLOCKS) {
      if (codes.has(b.code)) for (const [lon, lat] of b.ring) pts.push([lat, lon]);
    }
    if (pts.length === 0) return;
    map.fitBounds(L.latLngBounds(pts), {
      paddingTopLeft: [380, 130], paddingBottomRight: [60, 60],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fit on tenant switch only
  }, [fitKey, map]);
  return null;
}

// ── Map interaction layer (reports marker screen pos to parent) ────
function MapInteractionLayer({
  plants, onMarkerScreenPos,
}: {
  plants: Plant[];
  onMarkerScreenPos: (positions: Record<number, MarkerScreenPos>) => void;
}): null {
  const map = useMap();

  useEffect(() => {
    function updatePositions(): void {
      const positions: Record<number, MarkerScreenPos> = {};
      for (const plant of plants) {
        if (plant.lat == null || plant.lon == null) continue;
        const pt = map.latLngToContainerPoint([Number(plant.lat), Number(plant.lon)]);
        positions[plant.id] = { x: pt.x, y: pt.y };
      }
      onMarkerScreenPos(positions);
    }
    updatePositions();
    map.on("resize", updatePositions);
    map.on("moveend", updatePositions);
    map.on("zoomend", updatePositions);
    return () => {
      map.off("resize", updatePositions);
      map.off("moveend", updatePositions);
      map.off("zoomend", updatePositions);
    };
  }, [map, plants, onMarkerScreenPos]);

  return null;
}
